// Subscription screen of the account panel: a section per delivery day + time with
// a box per meal type. Quality has no backend for changes yet, so it's read-only.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { COLORS, FONTS } from 'styles/theme';
import { SUBSCRIPTION_BASE_PRICES, SUBSCRIPTION_DISCOUNT } from 'components/storefront/SubscriptionMemberPanel';
import ResumeDatePicker from 'components/shared/ResumeDatePicker';
import DeliveryAddressFields from 'components/shared/DeliveryAddressFields';
import { formatDeliveryDate, getResumeDates } from 'utils/deliveryDates';
import { preferredSlot, slotKey, slotLabel } from 'utils/deliverySlots';

const { dark } = COLORS;
const MUTED = '#8a8d91';
const ORANGE = '#f9a826';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Thursday'];
const TIMES = ['Morning', 'Midday', 'Afternoon', 'Evening'];
const TIERS = [
  { key: 'standard', label: 'Standard' },
  { key: 'premium', label: 'Organic · Wild-caught · Grass-fed' },
];
const MAX_MEALS = 50;
const MEAL_ORDER = ['breakfast', 'lunch', 'dinner', 'snack'];
const isSnack = (mealType) => /snack/i.test(mealType || '');
const mealRank = (mealType) => {
  const i = MEAL_ORDER.indexOf(String(mealType || '').toLowerCase().replace(/s$/, ''));
  return i < 0 ? MEAL_ORDER.length : i;
};

const FIELD = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: '15px',
  border: '1px solid #d8d0bd', borderRadius: '8px', background: '#fff',
  color: dark, fontFamily: FONTS.sans,
};

const linkBtn = (color = dark) => ({
  background: 'none', border: 'none', padding: 0, cursor: 'pointer', color,
  fontSize: '12px', fontWeight: 700, textDecoration: 'underline', fontFamily: FONTS.sans,
});

const unitPrice = (mealType) => {
  const key = String(mealType || '').toLowerCase().replace(/s$/, '');
  const base = SUBSCRIPTION_BASE_PRICES[key];
  return base ? base * SUBSCRIPTION_DISCOUNT : 0;
};

const money = (n) => `$${n.toFixed(2)}`;

const groupKeyOf = (s) => `${s.deliveryDay}|${s.deliveryTime}`;
const zip5 = (d) => String(d?.zip || '').trim().slice(0, 5);

const addressDraftOf = (s) => ({
  address: s.address.line1 || '', address2: s.address.line2 || '', city: s.address.city || '',
  state: s.address.state || '', zip: s.address.zip || '', phone: s.address.phone || '',
});

function Stepper({ value, onChange, min = 1, max = MAX_MEALS, label }) {
  const btn = (disabled) => ({
    width: '36px', height: '36px', borderRadius: '50%', border: '1px solid #d8d0bd',
    background: '#fff', color: dark, fontSize: '20px', lineHeight: 1, cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.35 : 1, fontFamily: FONTS.sans,
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '18px' }}>
      <button type="button" aria-label={`Fewer ${label}`} disabled={value <= min} onClick={() => onChange(value - 1)} style={btn(value <= min)}>−</button>
      <span style={{ minWidth: '32px', textAlign: 'center', fontSize: '22px', fontWeight: 700, color: dark }}>{value}</span>
      <button type="button" aria-label={`More ${label}`} disabled={value >= max} onClick={() => onChange(value + 1)} style={btn(value >= max)}>+</button>
    </div>
  );
}

function AddOnRow({ items, busy, onAdd }) {
  const ref = useRef(null);
  const drag = useRef({ active: false, startX: 0, startLeft: 0, moved: false });
  const [dragging, setDragging] = useState(false);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });

  // Touch scrolls natively; this adds click-and-drag for a mouse.
  const onPointerDown = (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    drag.current = { active: true, startX: e.clientX, startLeft: ref.current.scrollLeft, moved: false };
    setDragging(true);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX;
    if (Math.abs(dx) > 5) d.moved = true;
    ref.current.scrollLeft = d.startLeft - dx;
  };
  const endDrag = () => { drag.current.active = false; setDragging(false); };

  const navBtn = {
    flex: 1, height: '48px', borderRadius: '10px', border: '1px solid #d8d0bd', background: '#fff',
    color: dark, cursor: 'pointer', fontSize: '24px', lineHeight: 1, fontFamily: FONTS.sans,
  };

  return (
    <div style={{ minWidth: 0, maxWidth: '100%' }}>
      <div
        ref={ref}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onClickCapture={(e) => { if (drag.current.moved) { e.preventDefault(); e.stopPropagation(); drag.current.moved = false; } }}
        style={{
          display: 'flex', gap: '10px', overflowX: 'auto', padding: '2px 2px 8px', scrollbarWidth: 'none',
          scrollSnapType: dragging ? 'none' : 'x proximity', cursor: dragging ? 'grabbing' : 'grab',
          touchAction: 'pan-x pan-y', WebkitOverflowScrolling: 'touch', userSelect: dragging ? 'none' : 'auto',
        }}
      >
        {items.map((dish) => (
          <div key={dish.id} style={{
            flex: '0 0 132px', scrollSnapAlign: 'start', border: '1px solid #ececec', borderRadius: '10px',
            overflow: 'hidden', background: '#fff', display: 'flex', flexDirection: 'column',
          }}>
            <div style={{
              height: '84px', background: dish.imageUrl ? `center / cover no-repeat url(${dish.imageUrl})` : COLORS.cream,
            }} />
            <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
              <div style={{
                fontSize: '12px', lineHeight: 1.3, color: dark, display: '-webkit-box', WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>{dish.name}</div>
              <div style={{ fontSize: '12px', color: MUTED, marginTop: 'auto' }}>{money(dish.price)}</div>
              <button type="button" disabled={busy} onClick={() => onAdd(dish)} style={{
                padding: '5px 0', border: `1px solid ${ORANGE}`, borderRadius: '6px', background: '#fff4dc', color: dark,
                fontSize: '12px', fontWeight: 700, fontFamily: FONTS.sans, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
              }}>Add</button>
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
        <button type="button" aria-label="Scroll left" onClick={() => scroll(-1)} style={navBtn}>‹</button>
        <button type="button" aria-label="Scroll right" onClick={() => scroll(1)} style={navBtn}>›</button>
      </div>
    </div>
  );
}

const comingSoon = (
  <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', color: MUTED, marginLeft: '8px' }}>
    COMING SOON
  </span>
);

export default function SubscriptionScreen({ subscriptions, onSave, onCancel, onPause, onResume, onAddDay, onLoadAddOns, onAddAddOn, onLoadDeliveryOptions, onSignup }) {
  const [meals, setMeals] = useState({});          // recordId -> meals/week (only edited rows)
  const [groupDrafts, setGroupDrafts] = useState({}); // groupKey -> { deliveryDay, deliveryTime, ...address }
  const [editingGroup, setEditingGroup] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null); // groupKey
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [pausing, setPausing] = useState(null);     // groupKey with the pause form open
  const [resumeDate, setResumeDate] = useState('');
  const [addingDay, setAddingDay] = useState(null);  // groupKey whose add-day form is open
  const [catalog, setCatalog] = useState(null);
  const [newDay, setNewDay] = useState({ deliveryDay: '', deliveryTime: '' });
  const [dayOptions, setDayOptions] = useState(null);  // [{ day, times }] offered for the customer's zip
  const [slotsByZip, setSlotsByZip] = useState({});    // zip -> [{ day, time, fee }], null if the lookup failed

  const groups = useMemo(() => {
    const map = new Map();
    subscriptions.forEach((s) => {
      const key = groupKeyOf(s);
      if (!map.has(key)) map.set(key, { key, deliveryDay: s.deliveryDay, deliveryTime: s.deliveryTime, rows: [], addOns: [] });
      (s.isAddOn ? map.get(key).addOns : map.get(key).rows).push(s);
    });
    const dayIdx = (d) => { const i = DAYS.indexOf(d); return i < 0 ? 99 : i; };
    const timeIdx = (t) => { const i = TIMES.indexOf(t); return i < 0 ? 99 : i; };
    map.forEach((g) => g.rows.sort((a, b) => mealRank(a.mealType) - mealRank(b.mealType)));
    return [...map.values()].sort((a, b) =>
      dayIdx(a.deliveryDay) - dayIdx(b.deliveryDay) || timeIdx(a.deliveryTime) - timeIdx(b.deliveryTime));
  }, [subscriptions]);

  const mealsOf = (s) => (meals[s.recordId] ?? s.mealsIncluded);
  const groupTotal = (g) => g.rows.reduce((sum, s) => sum + mealsOf(s) * unitPrice(s.mealType), 0)
    + g.addOns.reduce((sum, s) => sum + mealsOf(s) * (s.dishPrice || 0), 0);
  const feeOf = (g) => (g.rows[0] || g.addOns[0]).deliveryFee || 0;
  const total = groups.reduce((sum, g) => sum + groupTotal(g) + feeOf(g), 0);
  const premium = subscriptions.some((s) => s.premium);
  const ownedSlots = new Set(groups.map((g) => `${g.deliveryDay}|${g.deliveryTime}`));
  const availableDays = (dayOptions || [])
    .map((d) => ({ day: d.day, slots: d.slots.filter((o) => !ownedSlots.has(`${d.day}|${o.time}`)) }))
    .filter((d) => d.slots.length > 0);

  const dirtyRows = subscriptions.filter((s) => (
    (meals[s.recordId] != null && meals[s.recordId] !== s.mealsIncluded) || groupDrafts[groupKeyOf(s)]
  ));
  // Other days' slots are left out so an edit can't merge two deliveries.
  const offeredSlots = (g, zip) => slotsByZip[zip]?.filter((s) => {
    const key = `${s.day}|${s.time}`;
    return key === g.key || !ownedSlots.has(key);
  });
  const slotOffered = (g, d) => {
    const offered = offeredSlots(g, zip5(d));
    return !offered || offered.some((s) => slotKey(s.day, s.time) === slotKey(d.deliveryDay, d.deliveryTime));
  };
  const draftInvalid = groups.some((g) => {
    const d = groupDrafts[g.key];
    return d && ((d.address && !(d.city && d.state && d.zip)) || !slotOffered(g, d));
  });

  const editingZip = editingGroup ? zip5(groupDrafts[editingGroup]) : '';
  useEffect(() => {
    if (!/^\d{5}$/.test(editingZip) || editingZip in slotsByZip) return undefined;
    let cancelled = false;
    onLoadDeliveryOptions(editingZip)
      .then((days) => {
        if (cancelled) return;
        const slots = days.flatMap((d) => d.slots.map((o) => ({ day: d.day, time: o.time, fee: o.cost })));
        setSlotsByZip((prev) => ({ ...prev, [editingZip]: slots }));
      })
      .catch((e) => {
        if (cancelled) return;
        setSlotsByZip((prev) => ({ ...prev, [editingZip]: null }));
        setError(e.message || 'Could not load delivery times.');
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingZip]);

  // Like checkout: when the address's slots arrive, a slot it doesn't offer falls back to the preferred one.
  useEffect(() => {
    const g = groups.find((x) => x.key === editingGroup);
    const d = groupDrafts[editingGroup];
    const offered = g && d ? offeredSlots(g, editingZip) : null;
    if (!offered?.length || slotOffered(g, d)) return;
    const p = preferredSlot(offered);
    setDraft(g, { deliveryDay: p.day, deliveryTime: p.time });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingGroup, editingZip, slotsByZip]);

  const startEdit = (g) => {
    setGroupDrafts((prev) => ({
      ...prev,
      [g.key]: prev[g.key] || { deliveryDay: g.deliveryDay, deliveryTime: g.deliveryTime, ...addressDraftOf(g.rows[0] || g.addOns[0]) },
    }));
    setEditingGroup(g.key);
    setSaved(false);
  };

  const setDraft = (g, patch) => setGroupDrafts((prev) => ({ ...prev, [g.key]: { ...prev[g.key], ...patch } }));

  const handleSave = async () => {
    setBusy(true);
    setError('');
    try {
      for (const s of dirtyRows) {
        const d = groupDrafts[groupKeyOf(s)];
        const patch = {};
        if (meals[s.recordId] != null && meals[s.recordId] !== s.mealsIncluded) patch.mealsIncluded = meals[s.recordId];
        if (d) Object.assign(patch, d);
        // Rows are saved one at a time; a failure leaves the earlier ones saved, and
        // the screen keeps the rest as unsaved edits so Save can simply be retried.
        // eslint-disable-next-line no-await-in-loop
        await onSave(s.recordId, patch);
        setMeals((prev) => { const { [s.recordId]: _drop, ...rest } = prev; return rest; });
      }
      setGroupDrafts({});
      setEditingGroup(null);
      setSaved(true);
    } catch (e) {
      setError(e.message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  const run = async (fn, fallback) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      return true;
    } catch (e) {
      setError(e.message || fallback);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handlePause = async (g) => {
    if (!resumeDate) { setError('Please pick the date to resume on.'); return; }
    const first = g.rows[0] || g.addOns[0];
    if (await run(() => onPause(first.nextDeliveryDate, resumeDate), 'Could not pause your subscription.')) {
      setPausing(null);
      setResumeDate('');
    }
  };

  useEffect(() => {
    let cancelled = false;
    onLoadAddOns().then((items) => { if (!cancelled) setCatalog(items); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Could not load add-ons.'); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddAddOn = async (g, dish) => {
    const first = g.rows[0] || g.addOns[0];
    await run(() => onAddAddOn(first.recordId, dish.id), 'Could not add that add-on.');
  };

  const openAddDay = (g) => {
    setAddingDay(g.key);
    setNewDay({ deliveryDay: '', deliveryTime: '' });
    setError('');
    if (dayOptions) return;
    onLoadDeliveryOptions().then(setDayOptions).catch((e) => setError(e.message || 'Could not load delivery days.'));
  };

  const handleAddDay = async () => {
    if (await run(() => onAddDay(newDay.deliveryDay, newDay.deliveryTime), 'Could not add that delivery day.')) {
      setAddingDay(null);
    }
  };

  const handleCancelGroup = async (g) => {
    setBusy(true);
    setError('');
    try {
      for (const s of [...g.rows, ...g.addOns]) {
        // eslint-disable-next-line no-await-in-loop
        await onCancel(s.recordId);
      }
      setConfirmCancel(null);
    } catch (e) {
      setError(e.message || 'Could not cancel your subscription.');
    } finally {
      setBusy(false);
    }
  };

  if (!groups.length) {
    return (
      <div style={{ textAlign: 'center', padding: '36px 16px', fontFamily: FONTS.sans }}>
        <p style={{ color: MUTED, fontSize: '14px', margin: '0 0 14px' }}>You don't have an active subscription yet.</p>
        <button type="button" onClick={onSignup} style={{
          padding: '12px 20px', border: 'none', borderRadius: '8px', background: ORANGE,
          color: '#fff', fontWeight: 700, fontSize: '15px', fontFamily: FONTS.sans, cursor: 'pointer',
        }}>Sign up for a subscription</button>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: FONTS.sans, display: 'flex', flexDirection: 'column', minHeight: '100%', minWidth: 0, maxWidth: '100%' }}>
      <div style={{ padding: '16px', borderBottom: '1px solid #ececec', background: COLORS.cream }}>
        <div style={{ fontSize: '11px', letterSpacing: '1px', color: MUTED }}>ESTIMATED WEEKLY TOTAL</div>
        <div style={{ fontSize: '28px', fontWeight: 700, color: dark, fontFamily: FONTS.serif }}>{money(total)}</div>
        <div style={{ marginTop: '12px', opacity: 0.6 }} aria-disabled="true">
          <div style={{ fontSize: '12px', color: dark, marginBottom: '6px', fontWeight: 700 }}>Protein{comingSoon}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {TIERS.map((t) => {
              const on = (t.key === 'premium') === premium;
              return (
                <button key={t.key} type="button" disabled style={{
                  padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontFamily: FONTS.sans,
                  border: `1px solid ${on ? ORANGE : '#d8d0bd'}`, background: on ? '#fff4dc' : '#fff', color: dark,
                }}>{t.label}</button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '20px', flex: 1 }}>
        {groups.map((g) => {
          const d = groupDrafts[g.key];
          const editing = editingGroup === g.key;
          const day = d?.deliveryDay ?? g.deliveryDay;
          const time = d?.deliveryTime ?? g.deliveryTime;
          const first = g.rows[0] || g.addOns[0];
          const addr = first.address;
          const paused = first.isPaused;
          return (
            <section key={g.key}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px' }}>
                <h2 style={{ margin: 0, fontFamily: FONTS.serif, fontSize: '24px', color: dark }}>
                  {day} · {time}
                </h2>
                {!editing && <button type="button" onClick={() => startEdit(g)} style={linkBtn()}>Edit</button>}
              </div>
              <div style={{ fontSize: '13px', color: MUTED, margin: '4px 0 12px' }}>
                {money(groupTotal(g))} / week{feeOf(g) > 0 ? ` + ${money(feeOf(g))} delivery` : ''}
                {!editing && ` · ${addr.line1 ? [addr.line1, addr.city].filter(Boolean).join(', ') : 'No delivery address saved'}`}
              </div>

              {paused && (
                <div style={{ background: '#fff4dc', border: '1px solid #f3d9a4', borderRadius: '10px', padding: '10px 12px', margin: '0 0 12px', fontSize: '13px', color: dark }}>
                  Paused — next delivery {formatDeliveryDate(first.nextDeliveryDate)}.{' '}
                  <button type="button" disabled={busy} onClick={() => run(() => onResume(first), 'Could not resume.')} style={linkBtn()}>
                    {busy ? 'Resuming…' : 'Resume now'}
                  </button>
                </div>
              )}

              {editing && d && (
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ fontSize: '11px', letterSpacing: '1px', color: MUTED, marginTop: '4px' }}>DELIVERY ADDRESS</div>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    <DeliveryAddressFields
                      value={d}
                      onChange={(patch) => setDraft(g, patch)}
                      idPrefix={`subscription-${slotKey(g.deliveryDay, g.deliveryTime).replace('|', '-')}`}
                      showNotes={false}
                    />
                  </div>
                  <label style={{ display: 'block' }}>
                    <span style={{ fontSize: '12px', color: MUTED, display: 'block', marginBottom: '4px' }}>Delivery day &amp; time</span>
                    {(() => {
                      const zip = zip5(d);
                      const offered = offeredSlots(g, zip);
                      if (!/^\d{5}$/.test(zip)) return <span style={{ fontSize: '13px', color: MUTED }}>Pick an address to see delivery times.</span>;
                      if (offered === undefined) return <span style={{ fontSize: '13px', color: MUTED }}>Checking delivery times…</span>;
                      if (offered === null) return <span style={{ fontSize: '13px', color: MUTED }}>Could not load delivery times.</span>;
                      if (!offered.length) return <span style={{ fontSize: '13px', color: '#c0392b' }}>We don&apos;t deliver to that zip code yet.</span>;
                      return (
                        <select
                          value={slotKey(d.deliveryDay, d.deliveryTime)}
                          onChange={(e) => {
                            const slot = offered.find((s) => slotKey(s.day, s.time) === e.target.value);
                            if (slot) setDraft(g, { deliveryDay: slot.day, deliveryTime: slot.time });
                          }}
                          style={FIELD}
                        >
                          {offered.map((slot) => (
                            <option key={slotKey(slot.day, slot.time)} value={slotKey(slot.day, slot.time)}>
                              {slotLabel(slot)}{slot.fee > 0 ? ` · ${money(slot.fee)} delivery` : ''}
                            </option>
                          ))}
                        </select>
                      );
                    })()}
                  </label>
                  <button type="button" onClick={() => {
                    setGroupDrafts((prev) => { const { [g.key]: _drop, ...rest } = prev; return rest; });
                    setEditingGroup(null);
                  }} style={{ ...linkBtn(MUTED), justifySelf: 'start' }}>Discard day / time / address changes</button>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '10px' }}>
                {g.rows.map((s) => (
                  <div key={s.recordId} style={{
                    background: COLORS.cream, border: `1px solid ${COLORS.creamAlt}`, borderRadius: '12px', padding: '14px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{ fontFamily: FONTS.serif, fontSize: '18px', fontWeight: 700, color: dark }}>{s.mealType || 'Meal Plan'}</span>
                      {unitPrice(s.mealType) > 0 && (
                        <span style={{ fontSize: '12px', color: MUTED }}>{money(unitPrice(s.mealType))} each</span>
                      )}
                    </div>
                    <div style={{ margin: '12px 0 2px' }}>
                      <Stepper
                        value={mealsOf(s)}
                        label={s.mealType || 'meals'}
                        onChange={(n) => { setMeals((prev) => ({ ...prev, [s.recordId]: n })); setSaved(false); }}
                      />
                      <div style={{ textAlign: 'center', fontSize: '11px', color: MUTED, marginTop: '4px' }}>{isSnack(s.mealType) ? 'snacks / day' : 'meals / week'}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '8px' }}>
                <div style={{ padding: '12px 14px', borderRadius: '10px', border: '1px solid #d8d0bd', background: '#fff', fontSize: '13px', color: dark, minWidth: 0, overflow: 'hidden' }}>
                  <div style={{ fontWeight: 700 }}>Add-ons</div>
                  {g.addOns.map((a) => (
                    <div key={a.recordId} style={{ marginTop: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                        <span>{a.dishName || 'Add-on'}</span>
                        {a.dishPrice > 0 && <span style={{ color: MUTED }}>{money(mealsOf(a) * a.dishPrice)} / week</span>}
                      </div>
                      <div style={{ marginTop: '6px' }}>
                        <Stepper
                          value={mealsOf(a)}
                          label={a.dishName || 'add-on'}
                          onChange={(n) => { setMeals((prev) => ({ ...prev, [a.recordId]: n })); setSaved(false); }}
                        />
                      </div>
                      <div style={{ textAlign: 'center', marginTop: '4px' }}>
                        <button type="button" disabled={busy} onClick={() => run(() => onCancel(a.recordId), 'Could not remove that add-on.')} style={linkBtn(MUTED)}>Remove</button>
                      </div>
                    </div>
                  ))}
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '12px', color: MUTED, marginBottom: '6px' }}>Added weekly to this delivery and charged with your subscription.</div>
                    {!catalog && <div style={{ color: MUTED }}>Loading…</div>}
                    {catalog && catalog.length === 0 && <div style={{ color: MUTED }}>No add-ons available right now.</div>}
                    {catalog && catalog.length > 0 && <AddOnRow items={catalog} busy={busy} onAdd={(dish) => handleAddAddOn(g, dish)} />}
                  </div>
                </div>

      {addingDay === g.key ? (
        <div style={{ padding: '14px', borderRadius: '12px', border: '2px dashed #d8d0bd', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '10px' }}>
          <div style={{ fontSize: '15px', fontWeight: 700, color: dark }}>Add delivery day</div>
          {!dayOptions && <div style={{ fontSize: '13px', color: MUTED }}>Loading delivery days…</div>}
          {dayOptions && availableDays.length === 0 && (
            <div style={{ fontSize: '13px', color: MUTED }}>No other delivery days are available for your zip code.</div>
          )}
          {availableDays.length > 0 && (
            <select value={newDay.deliveryDay} onChange={(e) => setNewDay({ deliveryDay: e.target.value, deliveryTime: '' })} style={FIELD}>
              <option value="">Select a day…</option>
              {availableDays.map((d) => <option key={d.day} value={d.day}>{d.day}</option>)}
            </select>
          )}
          {newDay.deliveryDay && (
            <select value={newDay.deliveryTime} onChange={(e) => setNewDay({ ...newDay, deliveryTime: e.target.value })} style={FIELD}>
              <option value="">Select a time…</option>
              {(availableDays.find((d) => d.day === newDay.deliveryDay)?.slots || []).map((o) => <option key={o.time} value={o.time}>{o.time}{o.cost > 0 ? ` · ${money(o.cost)} delivery` : ''}</option>)}
            </select>
          )}
          <p style={{ margin: 0, fontSize: '12px', color: MUTED }}>
            Starts with your next weekly charge. It copies your current meal types at 1 per week, and you can change the counts after.
          </p>
          <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
            <button type="button" disabled={busy || !newDay.deliveryDay || !newDay.deliveryTime} onClick={handleAddDay} style={{
              padding: '8px 14px', border: 'none', borderRadius: '8px', background: ORANGE, color: '#fff', fontWeight: 700,
              fontSize: '13px', fontFamily: FONTS.sans, opacity: busy || !newDay.deliveryDay || !newDay.deliveryTime ? 0.5 : 1, cursor: busy || !newDay.deliveryDay || !newDay.deliveryTime ? 'default' : 'pointer',
            }}>{busy ? 'Adding…' : 'Add delivery day'}</button>
            <button type="button" onClick={() => setAddingDay(null)} style={linkBtn(MUTED)}>Never mind</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => openAddDay(g)} style={{
          padding: '14px', borderRadius: '12px', border: '2px dashed #d8d0bd', background: 'transparent',
          color: MUTED, fontFamily: FONTS.sans, fontSize: '15px', fontWeight: 700, cursor: 'pointer',
        }}>
          <div style={{ fontSize: '28px', lineHeight: 1 }}>+</div>
          Add delivery day
        </button>
      )}

                {!paused && (pausing === g.key ? (
                  <div style={{ padding: '12px 14px', borderRadius: '10px', border: '1px solid #d8d0bd', background: '#fff', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '8px' }}>
                    <div style={{ fontSize: '13px', color: dark, fontWeight: 700 }}>Pause until…</div>
                    <p style={{ margin: 0, fontSize: '12px', color: MUTED }}>
                      This removes all meals and customizations for {formatDeliveryDate(first.nextDeliveryDate)} and skips every delivery until the date you pick.
                    </p>
                    <ResumeDatePicker
                      allowed={getResumeDates(first.nextDeliveryDate, first.deliveryDay)}
                      value={resumeDate}
                      onChange={setResumeDate}
                      accent={ORANGE}
                    />
                    <div style={{ fontSize: '12px', color: MUTED }}>
                      {resumeDate ? `Resume on ${formatDeliveryDate(resumeDate)}` : `Pick a ${first.deliveryDay} to resume on.`}
                    </div>
                    <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                      <button type="button" disabled={busy || !resumeDate} onClick={() => handlePause(g)} style={{
                        padding: '8px 14px', border: 'none', borderRadius: '8px', background: ORANGE, color: '#fff', fontWeight: 700,
                        fontSize: '13px', fontFamily: FONTS.sans, opacity: busy || !resumeDate ? 0.5 : 1, cursor: busy || !resumeDate ? 'default' : 'pointer',
                      }}>{busy ? 'Pausing…' : 'Pause subscription'}</button>
                      <button type="button" onClick={() => { setPausing(null); setResumeDate(''); }} style={linkBtn(MUTED)}>Never mind</button>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => { setPausing(g.key); setResumeDate(''); setError(''); }} style={{
                    textAlign: 'left', padding: '12px 14px', borderRadius: '10px', border: '1px solid #d8d0bd',
                    background: '#fff', color: dark, fontSize: '13px', fontWeight: 700, fontFamily: FONTS.sans, cursor: 'pointer',
                  }}>Pause subscription</button>
                ))}
              </div>

              <div style={{ marginTop: '12px', paddingLeft: '15px' }}>
                {confirmCancel === g.key ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', color: dark }}>Cancel all {g.rows.length} meal plan{g.rows.length === 1 ? '' : 's'} for {g.deliveryDay} · {g.deliveryTime}?</span>
                    <button type="button" disabled={busy} onClick={() => handleCancelGroup(g)} style={linkBtn('#c0392b')}>
                      {busy ? 'Cancelling…' : 'Yes, cancel'}
                    </button>
                    <button type="button" onClick={() => setConfirmCancel(null)} style={linkBtn(MUTED)}>Never mind</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setConfirmCancel(g.key)} style={linkBtn(MUTED)}>Cancel subscription</button>
                )}
              </div>
            </section>
          );
        })}

      </div>

      <div style={{
        position: 'sticky', bottom: 0, background: '#fff', borderTop: '1px solid #ececec', padding: '12px 16px',
      }}>
        {error && <p style={{ color: '#c0392b', fontSize: '13px', margin: '0 0 8px' }} role="alert">{error}</p>}
        {saved && !dirtyRows.length && !error && <p style={{ color: '#2e7d32', fontSize: '13px', margin: '0 0 8px' }}>Saved.</p>}
        <button
          type="button"
          disabled={busy || !dirtyRows.length || draftInvalid}
          onClick={handleSave}
          style={{
            width: '100%', padding: '12px 20px', border: 'none', borderRadius: '8px', background: ORANGE,
            color: '#fff', fontWeight: 700, fontSize: '15px', fontFamily: FONTS.sans,
            cursor: busy || !dirtyRows.length || draftInvalid ? 'default' : 'pointer',
            opacity: busy || !dirtyRows.length || draftInvalid ? 0.5 : 1,
          }}
        >
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
